import { AccessRuleEntitlementContext } from "../models";
import {
  BlackoutDateRangeAccessRule,
  EditableBlackoutDateRangeAccessRule,
} from "../blackoutDateRange/blackoutDateRange";
import {
  BlackoutDayOfWeekAccessRule,
  EditableBlackoutDayOfWeekRuleGroup,
} from "../blackoutDayOfWeek/blackoutDayOfWeek";
import {
  EffectiveDateRangeAccessRule,
  EditableEffectiveDateRangeAccessRule,
} from "../effectiveDateRange/effectiveDateRange";
import {
  MaxDaysAccessRule,
  EditableMaxDaysAccessRule,
} from "../maxDays/maxDays";

export interface LiftAccessRuleSetContext {
  entitlementTypeId: number;
  entitlementDescription: string;

  sellingSeasonId: number;
  sellingSeasonDescription: string;

  fulfillmentSeasonId: number;
  fulfillmentSeasonDescription: string;

  locationId: number;
  locationName: string;
  locationType: string;
  scopeCategoryId: number;
}

export interface LiftAccessRules {
  effectiveDateRangeRules: EffectiveDateRangeAccessRule[];
  maxDaysRules: MaxDaysAccessRule[];
  blackoutDateRangeRules: BlackoutDateRangeAccessRule[];
  blackoutDayOfWeekRules: BlackoutDayOfWeekAccessRule[];
}

export interface LiftAccessRuleSet
  extends LiftAccessRuleSetContext, LiftAccessRules {}

/**
 * Stable identity key for a rule set, derived from its entitlement context
 * (entitlement type + fulfillment season + scope). Used for row selection and
 * to correlate rule sets across search results.
 */
export const liftAccessRuleSetKey = (
  ruleSet: Pick<
    LiftAccessRuleSetContext,
    | "entitlementTypeId"
    | "fulfillmentSeasonId"
    | "scopeCategoryId"
    | "locationId"
  >,
): string =>
  [
    ruleSet.entitlementTypeId,
    ruleSet.fulfillmentSeasonId,
    ruleSet.scopeCategoryId,
    ruleSet.locationId,
  ].join(":");

/**
 * Reads a context from an API response: camelCase (the API) or PascalCase (ours).
 * Undefined rather than partial — a wrong key mis-attributes it to another rule set.
 */
export const toEntitlementContext = (
  raw: any,
): AccessRuleEntitlementContext | undefined => {
  const entitlementTypeId = raw?.EntitlementTypeId ?? raw?.entitlementTypeId;
  const fulfillmentSeasonId =
    raw?.FulfillmentSeasonId ?? raw?.fulfillmentSeasonId;
  const scopeCategory =
    raw?.LiftAccessRuleScopeCategory ?? raw?.liftAccessRuleScopeCategory;
  const scopeEntityId =
    raw?.LiftAccessRuleScopeEntityId ?? raw?.liftAccessRuleScopeEntityId;

  if (
    entitlementTypeId == null ||
    fulfillmentSeasonId == null ||
    scopeCategory == null ||
    scopeEntityId == null
  ) {
    return undefined;
  }

  return {
    EntitlementTypeId: entitlementTypeId,
    FulfillmentSeasonId: fulfillmentSeasonId,
    LiftAccessRuleScopeCategory: scopeCategory,
    LiftAccessRuleScopeEntityId: scopeEntityId,
  };
};

/** A rule set's entitlement context, the shape every request and result keys on. */
export const ruleSetContext = (
  ruleSet: Pick<
    LiftAccessRuleSetContext,
    | "entitlementTypeId"
    | "fulfillmentSeasonId"
    | "scopeCategoryId"
    | "locationId"
  >,
): AccessRuleEntitlementContext => ({
  EntitlementTypeId: ruleSet.entitlementTypeId,
  FulfillmentSeasonId: ruleSet.fulfillmentSeasonId,
  LiftAccessRuleScopeCategory: ruleSet.scopeCategoryId,
  LiftAccessRuleScopeEntityId: ruleSet.locationId,
});

/** The same identity key as liftAccessRuleSetKey, from an entitlement context. */
export const entitlementContextKey = (
  context: AccessRuleEntitlementContext,
): string =>
  liftAccessRuleSetKey({
    entitlementTypeId: context.EntitlementTypeId,
    fulfillmentSeasonId: context.FulfillmentSeasonId,
    scopeCategoryId: context.LiftAccessRuleScopeCategory,
    locationId: context.LiftAccessRuleScopeEntityId,
  });

export interface EditableLiftAccessRules {
  effectiveDateRangeRules: EditableEffectiveDateRangeAccessRule[];
  maxDaysRules: EditableMaxDaysAccessRule[];
  blackoutDateRangeRules: EditableBlackoutDateRangeAccessRule[];
  blackoutDayOfWeekRuleGroups: EditableBlackoutDayOfWeekRuleGroup[];
}
